'use client';

import { type ChangeEvent, type InputHTMLAttributes } from 'react';

import {
  FieldGroup,
  type FieldGroupType,
} from '@/app/components/forms/field-group';
import {
  type CustomFormStyleConfig,
  getCustomFormStyle,
  inputBoxStyled,
  inputIconStyled,
  inputTextStyled,
  selectDropdownIconStyled,
  SelectNativeOptionGroupStyles,
  SelectNativeOptionStyles,
  selectPlaceholderTextStyled,
} from '@/app/components/forms/styles';
import { Icon, type IconType } from '@/app/components/icons';
import { IconArrowDropDown } from '@/app/components/icons/ui-actions/icon-arrow-drop-down';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';

export interface SelectOption {
  label?: string;
  value: string;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  group: string;
  options: SelectOption[];
}

interface SelectProps
  extends
    Omit<
      FieldGroupType,
      'length' | 'maxLength' | 'showMaxLength' | 'inputType'
    >,
    InputHTMLAttributes<HTMLSelectElement> {
  /**
   * The name of the input field.
   */
  name: string;
  /**
   * The options to be rendered as select options
   */
  options: SelectOption[] | SelectOptionGroup[];
  /**
   * (Optional) The default label to be displayed.
   */
  placeholder?: string;
  /**
   * (Optional) The value of the selected option.
   */
  value?: string;
  /**
   * (Optional) The icon to be displayed before the selec.
   */
  iconBefore?: IconType;
  /**
   * (Optional) The function to be called when the value changes.
   */
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  /**
   * (Optional) Whether the component is auto-completed.
   */
  autoComplete?: string;
  /**
   * (Optional) The class name of the component.
   */
  className?: string;

  /**
   * (Optional) The class name of the select element.
   */
  selectClassName?: string;

  /**
   * (Optional) The class name of the group element.
   */
  groupClassName?: string;

  /**
   * (Optional) Custom styles for the component.
   */
  customStyle?: CustomFormStyleConfig;

  hidePlaceholder?: boolean;
}

const renderChildren = (options: SelectOption[] | SelectOptionGroup[]) => {
  const isSelectOptionGroup = (
    option: SelectOption | SelectOptionGroup
  ): option is SelectOptionGroup => 'options' in option;

  const renderOptions = (options: SelectOption[]) =>
    options.map((option, index) => (
      <option
        key={index}
        className={SelectNativeOptionStyles}
        value={option.value}
        disabled={option.disabled}
      >
        {option.label}
      </option>
    ));

  const isGrouped = options.length > 0 && isSelectOptionGroup(options[0]);

  if (isGrouped) {
    return (options as SelectOptionGroup[]).map((group) => (
      <optgroup
        key={group.group}
        className={SelectNativeOptionGroupStyles}
        label={group.group}
      >
        {renderOptions(group.options)}
      </optgroup>
    ));
  }

  return renderOptions(options as SelectOption[]);
};

export const Select = ({
  label,
  secondaryLabel,
  errorMessage,
  message,
  theme = 'light',
  disabled = false,
  required = false,
  error = false,
  name,
  options = [],
  placeholder = '',
  value,
  autoComplete = 'off',
  onChange,
  iconBefore,
  customStyle,
  className,
  selectClassName,
  groupClassName,
  ref,
  hidePlaceholder,
  ...props
}: SelectProps) => {
  const [, { open: onOpenFormSelect, close: onCloseFormSelect }] =
    useDisclosure(false);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onCloseFormSelect();
    safeCall(onChange, event);
  };

  const handleBlur = () => {
    onCloseFormSelect();
  };

  const wrapperClass = cn(
    inputBoxStyled({
      theme,
      error,
      disabled,
    }),
    'group flex h-12 rounded-sm',
    getCustomFormStyle(customStyle?.select?.wrapper, { error, disabled }),
    className
  );

  const hasValue = value !== undefined && value !== '';
  const isPlaceholderVisible = !hasValue;

  const systemPlaceholderClass = isPlaceholderVisible
    ? selectPlaceholderTextStyled({ theme, error, disabled })
    : '';

  const customPlaceholderClass = getCustomFormStyle(
    isPlaceholderVisible
      ? customStyle?.select?.placeholder
      : customStyle?.select?.text,
    { error, disabled }
  );

  const selectClass = cn(
    inputTextStyled({
      theme,
      error,
      disabled,
    }),
    'cursor-pointer self-stretch overflow-hidden pr-12 pl-4 text-ellipsis',
    {
      'pl-12': !!iconBefore,
    },
    systemPlaceholderClass,
    customPlaceholderClass,
    selectClassName
  );

  const iconClass = cn(
    inputIconStyled({
      theme,
      error,
      disabled,
    }),
    'absolute top-0 bottom-0 left-4 my-auto',
    getCustomFormStyle(customStyle?.select?.icon, { error, disabled })
  );

  const dropdownIconClass = cn(
    selectDropdownIconStyled({
      theme,
      error,
      disabled,
    }),
    'center-y absolute right-4',
    {
      'text-gray-800': hasValue && !disabled && theme === 'light',
      'text-white': hasValue && !disabled && theme === 'dark',
      'group-focus-within:text-gray-200 group-hover:text-gray-200':
        !hasValue && !disabled && !error && theme === 'light',
      'group-focus-within:text-gray-400 group-hover:text-gray-400':
        !hasValue && !disabled && !error && theme === 'dark',
    },
    getCustomFormStyle(customStyle?.select?.dropdownIcon, {
      error,
      disabled,
    })
  );

  return (
    <FieldGroup
      {...props}
      htmlFor={name}
      label={label}
      secondaryLabel={secondaryLabel}
      required={required}
      error={error}
      errorMessage={errorMessage}
      message={message}
      theme={theme}
      customStyle={customStyle?.fieldGroup}
      className={groupClassName}
      ref={label ? ref : undefined}
    >
      <div className={wrapperClass} ref={label ? undefined : ref}>
        {!!iconBefore && <Icon icon={iconBefore} className={iconClass} />}
        <Icon icon={<IconArrowDropDown />} className={dropdownIconClass} />
        <select
          id={name}
          name={name}
          value={value}
          disabled={disabled}
          autoComplete={autoComplete}
          onMouseDown={() => onOpenFormSelect()}
          onBlur={handleBlur}
          onChange={handleChange}
          className={selectClass}
          {...createDataAttributes({ test: `select-${name}` })}
        >
          {!hidePlaceholder && (
            <option className={SelectNativeOptionStyles} value="">
              {placeholder || 'กรุณาเลือก'}
            </option>
          )}
          {renderChildren(options)}
        </select>
      </div>
    </FieldGroup>
  );
};
